import { useCallback, useState } from 'react';

import api from './api';
import { CheckoutSession, Plan, Subscription } from '../models';

export const useSubscription = () => {
  const [loading, setLoading] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  const getPlans = useCallback((): Promise<Plan[]> => {
    setLoading(true);
    return api
      .get<{ plans: Plan[] }>('/v1/plans/')
      .then((response) => {
        setPlans(response.data.plans);
        setLoading(false);
        return response.data.plans;
      })
      .catch((error) => {
        setLoading(false);
        return Promise.reject(error);
      });
  }, []);

  const getSubscription = useCallback((): Promise<Subscription> => {
    setLoading(true);
    return api
      .get<Subscription>('/v1/subscription/')
      .then((response) => {
        setSubscription(response.data);
        setLoading(false);
        return response.data;
      })
      .catch((error) => {
        setLoading(false);
        return Promise.reject(error);
      });
  }, []);

  const changePlan = useCallback((planSlug: string): Promise<Subscription> => {
    setChangingPlan(true);
    return api
      .post<Subscription>('/v1/subscription/change/', {
        plan_slug: planSlug,
      })
      .then((response) => {
        setSubscription(response.data);
        setChangingPlan(false);
        return response.data;
      })
      .catch((error) => {
        setChangingPlan(false);
        return Promise.reject(error);
      });
  }, []);

  const createCheckout = useCallback(
    (planSlug: string): Promise<CheckoutSession> => {
      setCreatingCheckout(true);
      const origin = window.location.origin;
      return api
        .post<CheckoutSession>('/v1/billing/checkout/', {
          plan_slug: planSlug,
          success_url: `${origin}/admin/subscription?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
          failure_url: `${origin}/admin/subscription?checkout=failure`,
        })
        .then((response) => {
          setCreatingCheckout(false);
          return response.data;
        })
        .catch((error) => {
          setCreatingCheckout(false);
          return Promise.reject(error);
        });
    },
    []
  );

  const getCheckoutStatus = useCallback(
    (sessionId: string): Promise<CheckoutSession> => {
      return api
        .get<CheckoutSession>(`/v1/billing/checkout/${sessionId}/`)
        .then((response) => response.data);
    },
    []
  );

  const loadPlanManagement = useCallback((): Promise<void> => {
    setLoading(true);
    return Promise.all([
      api.get<{ plans: Plan[] }>('/v1/plans/'),
      api.get<Subscription>('/v1/subscription/'),
    ])
      .then(([plansResponse, subscriptionResponse]) => {
        setPlans(plansResponse.data.plans);
        setSubscription(subscriptionResponse.data);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        return Promise.reject(error);
      });
  }, []);

  return {
    loading,
    changingPlan,
    creatingCheckout,
    plans,
    subscription,
    getPlans,
    getSubscription,
    changePlan,
    createCheckout,
    getCheckoutStatus,
    loadPlanManagement,
  };
};
